import { clsx } from "clsx";
import styles from "./ThemeToggle.module.css";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ReactComponent as Sun } from "./icons/sun.svg";
import { ReactComponent as Moon } from "./icons/moon.svg";
import { ReactComponent as Auto } from "./icons/auto.svg";
import { atom, useAtom, useSetAtom } from "jotai";

interface ThemeToggleProps {
  className?: string;
}

const THEME_OPTIONS = ["Auto", "Light", "Dark"] as const;
const PREFERRED_COLOR_SCHEME_KEY = "preferred-color-scheme";

const readRawPref = () => {
  if (typeof window === "undefined") return "Auto";
  return window.localStorage.getItem(PREFERRED_COLOR_SCHEME_KEY) || "Auto";
};

const isSystemDark = () =>
  typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;

export const getCurrentTheme = () => {
  const themeSelection = readRawPref();
  return themeSelection === THEME_OPTIONS[0] ? (isSystemDark() ? "Dark" : "Light") : themeSelection;
};

// RESOLVED theme ("Light" | "Dark") — read by the app for surface
// colours. ThemeSync is the ONLY component that should read this and
// write ``data-color-scheme``; other toggles set it but don't touch
// the DOM directly. Keeping this to a single writer stops the back-
// button flicker caused by 3+ components racing to set the attribute.
export const themeAtom = atom<string>(getCurrentTheme());

// RAW preference ("Auto" | "Light" | "Dark") — drives the toggle icon
// and the segment picker selection state. All theme UIs read + write
// this atom so multiple mounted toggles stay in lockstep and survive
// a back-forward-cache restore without drifting from localStorage.
export const rawThemeAtom = atom<string>(readRawPref());
export const ThemeToggle: React.FC<ThemeToggleProps> = ({ className }) => {
  // RAW preference read from the shared atom — kept in lockstep with
  // ThemeSegment and any other toggle mount via localStorage +
  // storage-event fan-out (see ThemeSync). No component-local
  // ``useState`` here: that was the source of the "toggle icon still
  // shows Auto after ThemeSegment changed it to Dark" drift.
  const [theme, setRawTheme] = useAtom(rawThemeAtom);
  const setThemeAtom = useSetAtom(themeAtom);

  const themeChanged = useCallback(() => {
    const length = THEME_OPTIONS.length;
    const index = (THEME_OPTIONS.indexOf(theme as (typeof THEME_OPTIONS)[number]) + 1) % length;
    const nextTheme = THEME_OPTIONS[index];

    window.localStorage.setItem(PREFERRED_COLOR_SCHEME_KEY, nextTheme);
    setRawTheme(nextTheme);
    // Resolve the raw preference to a concrete "Light"/"Dark" and
    // hand it to the resolved atom. ThemeSync watches this atom and
    // is the only writer of ``data-color-scheme`` — do NOT set the
    // DOM attribute here (multiple writers = flicker on nav).
    const resolved = nextTheme === "Auto" ? (isSystemDark() ? "Dark" : "Light") : nextTheme;
    setThemeAtom(resolved);
  }, [theme, setRawTheme, setThemeAtom]);

  const themeLabel = useMemo(
    () => THEME_OPTIONS.find((option) => option.toLowerCase() === theme.toLowerCase()),
    [theme],
  );

  // Icon is driven by the user's *selected* theme (including "Auto")
  // rather than the resolved applied theme. This way each click of the
  // toggle always swaps the visible icon, so the user gets immediate
  // visual feedback — no two-clicks-of-the-sun ambiguity when Auto
  // happens to resolve to Light.
  return (
    <button
      className={clsx(styles.themeToggle, className, {
        [styles.auto]: theme === "Auto",
        [styles.light]: theme === "Light",
        [styles.dark]: theme === "Dark",
      })}
      onClick={themeChanged}
      type="button"
      title={`Theme: ${themeLabel} — click to cycle`}
      aria-label={`Theme: ${themeLabel}. Click to change theme.`}
    >
      <div className={clsx(styles.themeToggle__icon)}>
        <div className={clsx(styles.animationWrapper)}>
          <Auto className={clsx(styles.auto)} />
          <Sun className={clsx(styles.sun)} />
          <Moon className={clsx(styles.moon)} />
        </div>
      </div>
      <span className={clsx(styles.themeToggle__text)}>{themeLabel}</span>
    </button>
  );
};

export default ThemeToggle;
